import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, register as apiRegister, logout as apiLogout, refreshSession, type UserRole } from '../api/auth.api';
import { can } from './usePermissions';
import { useAppStore } from '../store/appStore';
import { fetchWorkspaces } from '../api/workspace.api';
import { setActiveWorkspaceId as setClientWorkspaceId, setLogoutHandler } from '../api/client';

/** Where a user with a temp password is parked until they pick a real one. */
export const CHANGE_PASSWORD_PATH = '/change-password';

export const useAuth = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthLoading,
    setUser,
    setAuthLoading,
    setWorkspaces,
    setActiveWorkspaceId,
    reset,
  } = useAppStore();

  // Set up the global logout handler (called by the axios 401 interceptor).
  // Should only be installed once the user is actually authenticated.
  const installLogoutHandler = useCallback(() => {
    setLogoutHandler(() => {
      reset();
      setClientWorkspaceId(null);
      window.localStorage.removeItem('vina_active_workspace');
      navigate('/login', { replace: true });
    });
  }, [navigate, reset]);

  const handlePostLogin = useCallback(
    /**
     * @param role of the user who just signed in. Read from the response
     *   rather than the store, which the caller has only just written to.
     */
    async (redirectToDashboard: boolean, role: UserRole | null) => {
      /**
       * The first screen this role can actually open, in nav order: the agent
       * list, else the dashboard, else Leads. LEAD_MANAGER is scoped to Leads
       * alone and MEMBER to the dashboard and Leads (see usePermissions.ts), so
       * a single hard-coded landing path would drop one of them on a screen
       * that immediately renders "you don't have access". CUSTOMER holds the
       * agent list but neither of the others, so without a workspace it is
       * parked on Profile, which every role can reach.
       *
       * Derived from the matrix rather than role names, so a role's landing
       * follows whatever its nav is, with no second place to keep in sync.
       */
      const landingPath = (workspaceId?: string) => {
        if (workspaceId && can(role, 'agents.view')) return `/workspaces/${workspaceId}/agents`;
        if (can(role, 'dashboard.view')) return '/dashboard';
        if (can(role, 'leads.view')) return '/leads';
        return '/profile';
      };

      // Load workspaces and pick active one
      try {
        const workspaces = await fetchWorkspaces();
        setWorkspaces(workspaces);

        if (workspaces.length > 0) {
          const savedId = window.localStorage.getItem('vina_active_workspace');
          const existing =
            (savedId && workspaces.find((w) => w.id === savedId)) || workspaces[0];
          setActiveWorkspaceId(existing.id);
          setClientWorkspaceId(existing.id);

          if (redirectToDashboard) {
            navigate(landingPath(existing.id), { replace: true });
          }
        } else if (redirectToDashboard) {
          // No workspace to open an agent list in — fall through to the rest.
          navigate(landingPath(), { replace: true });
        }
      } catch (err) {
        // Workspaces will show their own empty/error states
        if (redirectToDashboard) {
          navigate(landingPath(), { replace: true });
        }
      }
    },
    [navigate, setActiveWorkspaceId, setWorkspaces],
  );

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      setAuthLoading(true);
      try {
        const { user: loggedInUser, mustChangePassword } = await apiLogin({ email, password });
        setUser(loggedInUser);
        installLogoutHandler();

        // Invited users (and anyone whose password was reset) land on a temp
        // password and must replace it before seeing anything else.
        if (mustChangePassword || loggedInUser.mustChangePassword) {
          navigate(CHANGE_PASSWORD_PATH, { replace: true });
          return;
        }

        await handlePostLogin(true, loggedInUser.role);
      } finally {
        setAuthLoading(false);
      }
    },
    [handlePostLogin, installLogoutHandler, navigate, setAuthLoading, setUser],
  );

  const handleRegister = useCallback(
    async (name: string, email: string, password: string) => {
      setAuthLoading(true);
      try {
        const { user: registeredUser, mustChangePassword } = await apiRegister({ name, email, password });
        setUser(registeredUser);
        installLogoutHandler();

        if (mustChangePassword || registeredUser.mustChangePassword) {
          navigate(CHANGE_PASSWORD_PATH, { replace: true });
          return;
        }

        await handlePostLogin(true, registeredUser.role);
      } finally {
        setAuthLoading(false);
      }
    },
    [handlePostLogin, installLogoutHandler, navigate, setAuthLoading, setUser],
  );

  const handleLogout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      reset();
      setClientWorkspaceId(null);
      window.localStorage.removeItem('vina_active_workspace');
      navigate('/login', { replace: true });
    }
  }, [navigate, reset]);

  /**
   * Called by the forced password-change screen once POST /auth/change-password
   * succeeds: clears the stored flag (the backend already cleared its own) and
   * continues into the normal post-login flow.
   */
  const completePasswordChange = useCallback(async () => {
    if (user) {
      setUser({ ...user, mustChangePassword: false });
    }
    await handlePostLogin(true, user?.role ?? null);
  }, [handlePostLogin, setUser, user]);

  const initialiseAuth = useCallback(async () => {
    if (user) return;
    setAuthLoading(true);

    try {
      const { user: refreshedUser } = await refreshSession();
      setUser(refreshedUser);

      // Only install the logout handler AFTER a successful session restore.
      // This prevents unauthenticated visitors on public pages from being
      // redirected to /login when the refresh 401 triggers the interceptor.
      installLogoutHandler();

      await handlePostLogin(false, refreshedUser.role);
    } catch (_err) {
      // Not logged in; stay on current page - this is expected if no session exists
    } finally {
      // Ensure loading is always reset
      setAuthLoading(false);
    }
  }, [handlePostLogin, installLogoutHandler, setAuthLoading, setUser, user]);

  return {
    user,
    isAuthLoading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    completePasswordChange,
    initialiseAuth,
  };
};
